import { useEffect, useState } from 'react'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppBase, appPath } from '../components/appBase'
import { eventService } from '../services/eventService'
import type { Session, Speaker } from '../data/types'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import SessionCard from '../components/SessionCard'
import { resolveAssetUrl } from '../services/assetUrl'

export default function SpeakerDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const base = useAppBase()
  const [speaker, setSpeaker] = useState<Speaker | undefined>()
  const [sessions, setSessions] = useState<Session[]>([])
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      eventService.getSpeaker(id),
      eventService.getSessions(),
      eventService.getSpeakers(),
    ]).then(([sp, ss, all]) => {
      setSpeaker(sp)
      setSessions(ss)
      setSpeakers(all)
      setLoading(false)
    })
  }, [id])

  if (loading) return <LoadingState />
  if (!speaker)
    return (
      <PageContainer>
        <EmptyState
          title="Speaker not found"
          action={
            <Button variant="contained" onClick={() => navigate(appPath(base, 'speakers'))}>
              All speakers
            </Button>
          }
        />
      </PageContainer>
    )

  const speakerSessions = sessions.filter((s) => s.speakerIds.includes(speaker.id))

  return (
    <PageContainer maxWidth="md">
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'text.secondary' }}
      >
        Back
      </Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            bgcolor: speaker.avatarColor,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '2rem',
            flexShrink: 0,
            overflow: 'hidden',
            backgroundImage: speaker.photoUrl
              ? `url(${resolveAssetUrl(speaker.photoUrl)})`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {speaker.photoUrl ? '' : speaker.initials}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {speaker.name}
          </Typography>
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            {speaker.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {speaker.company}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Biography
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {speaker.bio}
      </Typography>

      {speakerSessions.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Sessions
          </Typography>
          <Stack spacing={1.5}>
            {speakerSessions.map((s) => (
              <SessionCard key={s.id} session={s} speakers={speakers} />
            ))}
          </Stack>
        </>
      )}
    </PageContainer>
  )
}
